import { api } from './apiClient';

/**
 * Material Conversion / Reclassification.
 *
 * Mirrors src/models/MaterialConversion.js. The `preview` call is the
 * important one: it resolves the conversion rule, values the source from real
 * stock and derives the destination quantity and cost — so the workspace shows
 * those numbers rather than asking anyone to multiply a conversion factor.
 */

export type ConversionType =
  | 'RECLASSIFICATION' | 'PROCESS_CONVERSION' | 'GRADE_CHANGE' | 'UOM_REPACK' | 'PACKING_CONVERSION';

export type ConversionStatus = 'DRAFT' | 'SUBMITTED' | 'APPROVED' | 'POSTED' | 'CANCELLED';

export interface ConversionRule {
  id: number;
  rule_code: string;
  rule_name: string;
  description: string | null;
  conversion_type: ConversionType;
  source_product_id: number | null;
  source_product_type: string | null;
  source_product_name?: string | null;
  dest_product_id: number | null;
  dest_product_type: string | null;
  dest_product_name?: string | null;
  conversion_factor: number;
  allowed_loss_pct: number;
  requires_approval: number;
  requires_qc: number;
  gl_treatment: 'SAME_VALUE' | 'REVALUE' | 'EXPENSE_LOSS';
  is_active: number;
}

export interface ConversionPreviewLine {
  sourceProductId: number;
  destProductId: number;
  ruleId: number | null;
  ruleCode: string | null;
  ruleName: string | null;
  /** false when no configured rule permits this change. */
  allowed: boolean;
  reason: string | null;
  glTreatment: string | null;
  availableStock: number;
  sourceQuantity: number;
  sourceUnitCost: number;
  sourceValue: number;
  conversionFactor: number;
  lossQuantity: number;
  lossValue: number;
  lossPct: number;
  allowedLossPct: number;
  lossExceedsAllowance: boolean;
  destQuantity: number;
  destUnitCost: number;
  destValue: number;
  shortfall: number;
}

export interface ConversionPreview {
  lines: ConversionPreviewLine[];
  totals: { sourceValue: number; destValue: number; lossValue: number };
}

export interface ConversionListRow {
  id: number;
  conversion_number: string;
  conversion_date: string;
  conversion_type: ConversionType;
  status: ConversionStatus;
  reason: string | null;
  reason_code: string | null;
  total_source_value: number;
  total_dest_value: number;
  loss_value: number;
  line_count: number;
  rule_name: string | null;
  source_warehouse_name: string | null;
  dest_warehouse_name: string | null;
  created_by_name: string | null;
}

export interface ConversionItem {
  id: number;
  line_no: number;
  source_product_id: number;
  source_product_code: string;
  source_product_name: string;
  source_product_type: string;
  source_batch_number: string | null;
  source_quantity: number;
  source_uom: string | null;
  source_unit_cost: number;
  source_value: number;
  dest_product_id: number;
  dest_product_code: string;
  dest_product_name: string;
  dest_product_type: string;
  dest_batch_number: string | null;
  dest_quantity: number;
  dest_uom: string | null;
  dest_unit_cost: number;
  dest_value: number;
  conversion_factor: number;
  loss_quantity: number;
  loss_value: number;
  rule_code: string | null;
  rule_name: string | null;
  remarks: string | null;
}

export interface Conversion extends ConversionListRow {
  plant: string | null;
  rule_code: string | null;
  gl_treatment: string | null;
  allowed_loss_pct: number | null;
  journal_entry_id: number | null;
  journal_entry_number: string | null;
  remarks: string | null;
  posted_at: string | null;
  items: ConversionItem[];
}

export interface ConversionLineInput {
  sourceProductId: number;
  sourceBatchId?: number | null;
  sourceWarehouseId?: number | null;
  sourceQuantity: number;
  sourceUom?: string | null;
  destProductId: number;
  destBatchNumber?: string;
  destWarehouseId?: number | null;
  destQuantity?: number;
  destUom?: string | null;
  conversionFactor?: number;
  lossQuantity?: number;
  remarks?: string;
}

export const CONVERSION_TYPE_LABELS: Record<ConversionType, string> = {
  RECLASSIFICATION: 'Reclassification',
  PROCESS_CONVERSION: 'Process conversion',
  GRADE_CHANGE: 'Grade change',
  UOM_REPACK: 'Pack size / UOM',
  PACKING_CONVERSION: 'Packing conversion',
};

export const materialConversionService = {
  list: (params?: Record<string, any>) =>
    api.get<{ conversions: ConversionListRow[]; count: number }>('/material-conversions', { params }),

  get: (id: number | string) =>
    api.get<{ conversion: Conversion }>(`/material-conversions/${id}`),

  /** Resolves the rule, values the source and derives the destination. */
  preview: (payload: {
    conversionType: ConversionType;
    sourceWarehouseId?: number | null;
    lines: Array<Partial<ConversionLineInput> & { sourceProductId: number; destProductId: number }>;
  }) => api.post<ConversionPreview>('/material-conversions/preview', payload),

  create: (payload: {
    conversionDate: string;
    conversionType: ConversionType;
    plant?: string;
    sourceWarehouseId?: number | null;
    destWarehouseId?: number | null;
    reasonCode?: string;
    reason: string;
    remarks?: string;
    lines: ConversionLineInput[];
  }) => api.post<{ conversion: Conversion }>('/material-conversions', payload),

  reverse: (id: number | string, reason: string) =>
    api.post<{ conversion: Conversion }>(`/material-conversions/${id}/reverse`, { reason }),

  listRules: (params?: Record<string, any>) =>
    api.get<{ rules: ConversionRule[]; count: number }>('/material-conversions/rules', { params }),

  createRule: (payload: Partial<ConversionRule> & { ruleCode: string; ruleName: string }) =>
    api.post<{ id: number }>('/material-conversions/rules', payload),
};

export default materialConversionService;
